import * as TOML from "@iarna/toml";

import {
  ORGX_CODEX_MCP_URL,
  ORGX_CODEX_TOOL_PROFILE,
  ORGX_CODEX_TOOL_PROFILE_HEADER,
  ORGX_HOSTED_MCP_KEY,
  ORGX_HOSTED_MCP_URL,
  ORGX_LOCAL_MCP_KEY,
} from "../constants.js";
import { isRecord, parseJsonObject, readObject } from "../lib/fs.js";

type JsonObject = Record<string, unknown>;

const ORGX_LEGACY_CODEX_MCP_URL = `${ORGX_HOSTED_MCP_URL}?profile=commander`;

export interface McpConfigInspection {
  configured: boolean;
  hostedConfigured?: boolean;
  localConfigured?: boolean;
  details: string[];
}

function cloneConfigCollection(config: JsonObject, key: string): JsonObject {
  return { ...readObject(config[key]) };
}

function removeLegacyScopedMcpServers(servers: JsonObject): boolean {
  const scopedPrefix = `${ORGX_LOCAL_MCP_KEY}-`;
  let updated = false;

  for (const key of Object.keys(servers)) {
    if (key === ORGX_LOCAL_MCP_KEY) continue;
    if (!key.startsWith(scopedPrefix)) continue;
    delete servers[key];
    updated = true;
  }

  return updated;
}

function stringifyJsonConfig(config: JsonObject): string {
  return `${JSON.stringify(config, null, 2)}\n`;
}

function clearStdioFields(entry: JsonObject): JsonObject {
  const next = { ...entry };
  delete next.command;
  delete next.args;
  delete next.env;
  return next;
}

function matchHttpEntry(value: unknown, url: string): boolean {
  if (!isRecord(value)) return false;
  return value.type === "http" && value.url === url;
}

function matchUrlEntry(value: unknown, url: string): boolean {
  if (!isRecord(value)) return false;
  return value.url === url;
}

export function patchClaudeMcpConfig(
  input: string | null,
  localMcpUrl?: string,
): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");
  const existingOrgx = readObject(servers[ORGX_HOSTED_MCP_KEY]);
  const existingLocal = readObject(servers[ORGX_LOCAL_MCP_KEY]);
  const hostedUrl = typeof existingOrgx.url === "string" ? existingOrgx.url : "";
  const hostedType = typeof existingOrgx.type === "string" ? existingOrgx.type : "";
  const shouldSetHostedOrgx =
    !isRecord(servers[ORGX_HOSTED_MCP_KEY]) ||
    (hostedUrl === localMcpUrl && hostedType === "http");

  if (shouldSetHostedOrgx) {
    servers[ORGX_HOSTED_MCP_KEY] = {
      ...existingOrgx,
      type: "http",
      url: ORGX_HOSTED_MCP_URL,
      description:
        typeof existingOrgx.description === "string" &&
        existingOrgx.description.trim().length > 0
          ? existingOrgx.description
          : "OrgX cloud MCP (OAuth)",
    };
  }

  if (localMcpUrl) {
    servers[ORGX_LOCAL_MCP_KEY] = {
      ...existingLocal,
      type: "http",
      url: localMcpUrl,
      description:
        typeof existingLocal.description === "string" &&
        existingLocal.description.trim().length > 0
          ? existingLocal.description
          : "OrgX platform via local OpenClaw plugin (no OAuth)",
    };
  }

  removeLegacyScopedMcpServers(servers);

  return stringifyJsonConfig({
    ...current,
    mcpServers: servers,
  });
}

export function removeClaudeMcpConfig(
  input: string | null,
): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");

  if (isRecord(servers[ORGX_LOCAL_MCP_KEY])) {
    delete servers[ORGX_LOCAL_MCP_KEY];
  }

  if (matchHttpEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL)) {
    delete servers[ORGX_HOSTED_MCP_KEY];
  }

  removeLegacyScopedMcpServers(servers);

  return stringifyJsonConfig({
    ...current,
    mcpServers: servers,
  });
}

export function inspectClaudeMcpConfig(input: string | null): McpConfigInspection {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");
  const local = readObject(servers[ORGX_LOCAL_MCP_KEY]);
  const hostedConfigured = matchHttpEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL);
  const localConfigured =
    local.type === "http" &&
    typeof local.url === "string";
  const localUrl = localConfigured ? String(local.url) : "missing";

  return {
    configured: hostedConfigured && localConfigured,
    hostedConfigured,
    localConfigured,
    details: [
      `hosted orgx: ${hostedConfigured ? "ok" : "missing"}`,
      `local orgx-openclaw: ${localUrl}`,
    ],
  };
}

export function patchCursorMcpConfig(
  input: string | null,
  localMcpUrl: string,
): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");
  const existingLocal = readObject(servers[ORGX_LOCAL_MCP_KEY]);

  servers[ORGX_LOCAL_MCP_KEY] = {
    ...clearStdioFields(existingLocal),
    url: localMcpUrl,
  };

  removeLegacyScopedMcpServers(servers);

  return stringifyJsonConfig({
    ...current,
    mcpServers: servers,
  });
}

export function removeCursorMcpConfig(
  input: string | null,
): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");

  if (isRecord(servers[ORGX_LOCAL_MCP_KEY])) {
    delete servers[ORGX_LOCAL_MCP_KEY];
  }

  removeLegacyScopedMcpServers(servers);

  return stringifyJsonConfig({
    ...current,
    mcpServers: servers,
  });
}

export function inspectCursorMcpConfig(input: string | null): McpConfigInspection {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");
  const local = readObject(servers[ORGX_LOCAL_MCP_KEY]);
  const localConfigured = typeof local.url === "string";
  const localUrl = localConfigured ? String(local.url) : "missing";
  const hostedConfigured = matchHttpEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL);

  return {
    // Back-compat: "configured" on Cursor historically meant the OpenClaw bridge line only.
    configured: localConfigured,
    hostedConfigured,
    localConfigured,
    details: [
      `hosted orgx: ${hostedConfigured ? "ok" : "missing"}`,
      `local orgx-openclaw: ${localUrl}`,
    ],
  };
}

export function patchVscodeMcpConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "servers");
  const existingHosted = readObject(servers[ORGX_HOSTED_MCP_KEY]);

  servers[ORGX_HOSTED_MCP_KEY] = {
    ...clearStdioFields(existingHosted),
    type: "http",
    url: ORGX_HOSTED_MCP_URL,
  };

  return stringifyJsonConfig({
    ...current,
    servers,
  });
}

export function removeVscodeMcpConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "servers");

  if (matchHttpEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL)) {
    delete servers[ORGX_HOSTED_MCP_KEY];
  }

  return stringifyJsonConfig({
    ...current,
    servers,
  });
}

export function inspectVscodeMcpConfig(input: string | null): McpConfigInspection {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "servers");
  const hostedConfigured = matchHttpEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL);

  return {
    configured: hostedConfigured,
    hostedConfigured,
    details: [`hosted orgx: ${hostedConfigured ? "ok" : "missing"}`],
  };
}

function matchServerUrlEntry(value: unknown, url: string): boolean {
  if (!isRecord(value)) return false;
  return value.serverUrl === url || value.url === url;
}

export function patchWindsurfMcpConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");
  const existingHosted = readObject(servers[ORGX_HOSTED_MCP_KEY]);
  const nextEntry = clearStdioFields(existingHosted);

  delete nextEntry.url;
  servers[ORGX_HOSTED_MCP_KEY] = {
    ...nextEntry,
    serverUrl: ORGX_HOSTED_MCP_URL,
  };

  return stringifyJsonConfig({
    ...current,
    mcpServers: servers,
  });
}

export function removeWindsurfMcpConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");

  if (matchServerUrlEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL)) {
    delete servers[ORGX_HOSTED_MCP_KEY];
  }

  return stringifyJsonConfig({
    ...current,
    mcpServers: servers,
  });
}

export function inspectWindsurfMcpConfig(input: string | null): McpConfigInspection {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "mcpServers");
  const hostedConfigured = matchServerUrlEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL);

  return {
    configured: hostedConfigured,
    hostedConfigured,
    details: [`hosted orgx: ${hostedConfigured ? "ok" : "missing"}`],
  };
}

export function patchZedMcpConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "context_servers");
  const existingHosted = readObject(servers[ORGX_HOSTED_MCP_KEY]);

  servers[ORGX_HOSTED_MCP_KEY] = {
    ...clearStdioFields(existingHosted),
    url: ORGX_HOSTED_MCP_URL,
  };

  return stringifyJsonConfig({
    ...current,
    context_servers: servers,
  });
}

export function removeZedMcpConfig(input: string | null): string {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "context_servers");

  if (matchUrlEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL)) {
    delete servers[ORGX_HOSTED_MCP_KEY];
  }

  return stringifyJsonConfig({
    ...current,
    context_servers: servers,
  });
}

export function inspectZedMcpConfig(input: string | null): McpConfigInspection {
  const current = parseJsonObject(input);
  const servers = cloneConfigCollection(current, "context_servers");
  const hostedConfigured = matchUrlEntry(servers[ORGX_HOSTED_MCP_KEY], ORGX_HOSTED_MCP_URL);

  return {
    configured: hostedConfigured,
    hostedConfigured,
    details: [`hosted orgx: ${hostedConfigured ? "ok" : "missing"}`],
  };
}

function parseTomlDocument(input: string | null): JsonObject {
  if (!input) return {};

  try {
    const parsed = TOML.parse(input) as unknown;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function stringifyTomlDocument(document: JsonObject): string {
  const rendered = TOML.stringify(document as TOML.JsonMap);
  return rendered.endsWith("\n") ? rendered : `${rendered}\n`;
}

function upsertCodexEntry(
  servers: JsonObject,
  key: string,
  url: string,
  oauthResource?: string,
  toolProfile?: string,
): void {
  const current = readObject(servers[key]);
  const next: JsonObject = {
    ...current,
    url,
  };
  if (oauthResource) {
    next.oauth_resource = oauthResource;
  }
  if (toolProfile) {
    const httpHeaders = readObject(next.http_headers);
    httpHeaders[ORGX_CODEX_TOOL_PROFILE_HEADER] = toolProfile;
    next.http_headers = httpHeaders;
  }
  delete next.command;
  delete next.args;
  delete next.startup_timeout_sec;
  servers[key] = next;
}

function removeLegacyCodexEntries(servers: JsonObject): boolean {
  const scopedPrefix = `${ORGX_LOCAL_MCP_KEY}-`;
  let updated = false;

  for (const key of Object.keys(servers)) {
    if (key === ORGX_LOCAL_MCP_KEY) continue;
    if (!key.startsWith(scopedPrefix)) continue;
    delete servers[key];
    updated = true;
  }

  return updated;
}

export function patchCodexConfigToml(
  input: string | null,
  localMcpUrl?: string,
): string {
  const current = parseTomlDocument(input);
  const servers = readObject(current.mcp_servers);

  upsertCodexEntry(
    servers,
    ORGX_HOSTED_MCP_KEY,
    ORGX_CODEX_MCP_URL,
    ORGX_HOSTED_MCP_URL,
    ORGX_CODEX_TOOL_PROFILE,
  );
  if (localMcpUrl) {
    upsertCodexEntry(servers, ORGX_LOCAL_MCP_KEY, localMcpUrl);
  }
  removeLegacyCodexEntries(servers);

  current.mcp_servers = servers;
  return stringifyTomlDocument(current);
}

export function migrateCodexHostedMcpConnection(input: string | null): string | null {
  if (!input) return input;

  const current = parseTomlDocument(input);
  const servers = readObject(current.mcp_servers);
  const hosted = readObject(servers[ORGX_HOSTED_MCP_KEY]);
  if (
    hosted.url !== ORGX_HOSTED_MCP_URL &&
    hosted.url !== ORGX_LEGACY_CODEX_MCP_URL
  ) {
    return input;
  }

  const lines = input.match(/.*(?:\r\n|\n|$)/g)?.filter((line) => line.length > 0) ?? [];
  let activeSection: "orgx" | "headers" | "other" = "other";
  let orgxSectionEnd = lines.length;
  let headersSectionStart = -1;
  let headersSectionEnd = lines.length;
  let urlIndex = -1;
  let oauthResourceIndex = -1;
  let inlineHeadersIndex = -1;
  let profileHeaderIndex = -1;

  for (const [index, line] of lines.entries()) {
    if (/^\s*\[/.test(line)) {
      if (activeSection === "orgx" && orgxSectionEnd === lines.length) {
        orgxSectionEnd = index;
      }
      if (activeSection === "headers" && headersSectionEnd === lines.length) {
        headersSectionEnd = index;
      }
      if (/^\s*\[mcp_servers\.(?:orgx|"orgx")\]\s*(?:#.*)?(?:\r?\n)?$/.test(line)) {
        activeSection = "orgx";
      } else if (/^\s*\[mcp_servers\.(?:orgx|"orgx")\.http_headers\]\s*(?:#.*)?(?:\r?\n)?$/.test(line)) {
        activeSection = "headers";
        headersSectionStart = index;
      } else {
        activeSection = "other";
      }
      continue;
    }
    if (activeSection === "orgx") {
      if (/^\s*url\s*=/.test(line)) urlIndex = index;
      if (/^\s*oauth_resource\s*=/.test(line)) oauthResourceIndex = index;
      if (/^\s*http_headers\s*=/.test(line)) inlineHeadersIndex = index;
    } else if (
      activeSection === "headers" &&
      /^\s*(?:"x-orgx-tool-profile"|x-orgx-tool-profile)\s*=/.test(line)
    ) {
      profileHeaderIndex = index;
    }
  }

  if (urlIndex < 0) return input;

  const urlMatch = lines[urlIndex]?.match(
    /^(\s*url\s*=\s*)(["'])(https:\/\/mcp\.useorgx\.com\/mcp(?:\?profile=commander)?)\2([ \t]*(?:#.*)?)(\r?\n)?$/,
  );
  if (!urlMatch) return input;

  let changed = false;
  if (urlMatch[3] !== ORGX_CODEX_MCP_URL) {
    lines[urlIndex] = `${urlMatch[1]}${urlMatch[2]}${ORGX_CODEX_MCP_URL}${urlMatch[2]}${urlMatch[4]}${urlMatch[5] ?? ""}`;
    changed = true;
  }

  const httpHeaders = readObject(hosted.http_headers);
  const configuredProfile = httpHeaders[ORGX_CODEX_TOOL_PROFILE_HEADER];
  if (
    inlineHeadersIndex >= 0 &&
    configuredProfile !== ORGX_CODEX_TOOL_PROFILE
  ) {
    return input;
  }

  if (oauthResourceIndex >= 0) {
    const oauthResourceMatch = lines[oauthResourceIndex]?.match(
      /^(\s*oauth_resource\s*=\s*)(["'])([^"']*)\2([ \t]*(?:#.*)?)(\r?\n)?$/,
    );
    if (!oauthResourceMatch) return input;
    if (oauthResourceMatch[3] !== ORGX_HOSTED_MCP_URL) {
      lines[oauthResourceIndex] = `${oauthResourceMatch[1]}${oauthResourceMatch[2]}${ORGX_HOSTED_MCP_URL}${oauthResourceMatch[2]}${oauthResourceMatch[4]}${oauthResourceMatch[5] ?? ""}`;
      changed = true;
    }
  } else {
    const lineEnding = urlMatch[5] ?? (input.includes("\r\n") ? "\r\n" : "\n");
    if (!urlMatch[5]) {
      lines[urlIndex] = `${lines[urlIndex]}${lineEnding}`;
    }
    const indent = urlMatch[1]?.match(/^\s*/)?.[0] ?? "";
    lines.splice(
      urlIndex + 1,
      0,
      `${indent}oauth_resource = "${ORGX_HOSTED_MCP_URL}"${lineEnding}`,
    );
    changed = true;
  }

  const oauthInsertionShift = oauthResourceIndex < 0 ? 1 : 0;
  if (configuredProfile !== ORGX_CODEX_TOOL_PROFILE) {
    if (profileHeaderIndex >= 0) {
      const currentIndex = profileHeaderIndex + oauthInsertionShift;
      const profileHeaderMatch = lines[currentIndex]?.match(
        /^(\s*(?:"x-orgx-tool-profile"|x-orgx-tool-profile)\s*=\s*)(["'])([^"']*)\2([ \t]*(?:#.*)?)(\r?\n)?$/,
      );
      if (!profileHeaderMatch) return input;
      lines[currentIndex] = `${profileHeaderMatch[1]}${profileHeaderMatch[2]}${ORGX_CODEX_TOOL_PROFILE}${profileHeaderMatch[2]}${profileHeaderMatch[4]}${profileHeaderMatch[5] ?? ""}`;
    } else if (headersSectionStart >= 0) {
      lines.splice(
        headersSectionEnd + oauthInsertionShift,
        0,
        `${ORGX_CODEX_TOOL_PROFILE_HEADER} = "${ORGX_CODEX_TOOL_PROFILE}"${urlMatch[5] ?? "\n"}`,
      );
    } else if (readObject(hosted.http_headers)[ORGX_CODEX_TOOL_PROFILE_HEADER] === undefined) {
      const lineEnding = urlMatch[5] ?? (input.includes("\r\n") ? "\r\n" : "\n");
      const insertionIndex = orgxSectionEnd + oauthInsertionShift;
      lines.splice(
        insertionIndex,
        0,
        `[mcp_servers.orgx.http_headers]${lineEnding}`,
        `${ORGX_CODEX_TOOL_PROFILE_HEADER} = "${ORGX_CODEX_TOOL_PROFILE}"${lineEnding}`,
        lineEnding,
      );
    }
    changed = true;
  }

  return changed ? lines.join("") : input;
}

export function removeCodexConfigToml(
  input: string | null,
): string {
  const current = parseTomlDocument(input);
  const servers = readObject(current.mcp_servers);
  const hosted = readObject(servers[ORGX_HOSTED_MCP_KEY]);

  if (isRecord(servers[ORGX_LOCAL_MCP_KEY])) {
    delete servers[ORGX_LOCAL_MCP_KEY];
  }

  if (
    hosted.url === ORGX_HOSTED_MCP_URL ||
    hosted.url === ORGX_LEGACY_CODEX_MCP_URL
  ) {
    delete servers[ORGX_HOSTED_MCP_KEY];
  }

  removeLegacyCodexEntries(servers);
  current.mcp_servers = servers;

  return stringifyTomlDocument(current);
}

export function inspectCodexConfigToml(input: string | null): McpConfigInspection {
  const current = parseTomlDocument(input);
  const servers = readObject(current.mcp_servers);
  const hosted = readObject(servers[ORGX_HOSTED_MCP_KEY]);
  const local = readObject(servers[ORGX_LOCAL_MCP_KEY]);
  const hostedConfigured =
    hosted.url === ORGX_CODEX_MCP_URL &&
    hosted.oauth_resource === ORGX_HOSTED_MCP_URL &&
    readObject(hosted.http_headers)[ORGX_CODEX_TOOL_PROFILE_HEADER] ===
      ORGX_CODEX_TOOL_PROFILE;
  const localConfigured = typeof local.url === "string";

  return {
    configured: hostedConfigured && localConfigured,
    hostedConfigured,
    localConfigured,
    details: [
      `hosted orgx: ${hostedConfigured ? "ok" : "missing"}`,
      `local orgx-openclaw: ${localConfigured ? local.url : "missing"}`,
    ],
  };
}
